import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { ProgressBar } from 'primereact/progressbar';
import { Divider } from 'primereact/divider';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { getServerMastersList, previewBranchTables, createBranchTables } from '../utilities/api';
import '../custom.css';

const TABLE_BASES = [
    { key: 'product_billdetail', label: 'Bill Details' },
    { key: 'billed_product', label: 'Billed Products' },
    { key: 'payments_details', label: 'Payment Details' }
];

const StatTile = ({ label, value, color, icon }) => (
    <div className="flex align-items-center gap-3 border-round shadow-1 p-3 surface-card" style={{ borderLeft: `4px solid ${color}`, minWidth: 150 }}>
        <div className="flex align-items-center justify-content-center border-round-lg" style={{ width: 40, height: 40, background: `${color}1a` }}>
            <i className={`pi ${icon}`} style={{ fontSize: '1.2rem', color }} />
        </div>
        <div>
            <div className="text-2xl font-bold line-height-1">{value}</div>
            <div className="text-color-secondary text-sm">{label}</div>
        </div>
    </div>
);

const ExistTag = (tableName, exists) => (
    <Tag severity={exists ? 'success' : 'warning'} icon={exists ? 'pi pi-check' : 'pi pi-exclamation-triangle'} value={tableName} />
);

const BranchTables = () => {
    const [clients, setClients] = useState([]);
    const [loadingLookups, setLoadingLookups] = useState(false);

    const [client, setClient] = useState(null);

    const [previewBranches, setPreviewBranches] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewKey, setPreviewKey] = useState(null);
    const [connectError, setConnectError] = useState(null);

    const [createResults, setCreateResults] = useState(null);
    const [createLoading, setCreateLoading] = useState(false);
    const [createProgress, setCreateProgress] = useState(null);

    const toast = useRef(null);

    useEffect(() => {
        const loadLookups = async () => {
            setLoadingLookups(true);
            try {
                const res = await getServerMastersList('client_creation');
                const rows = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
                setClients(rows.map((c) => ({ label: `${c.client_name} (${c.client_id})`, value: String(c.client_id) })));
            } catch (error) {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Could not load clients' });
            } finally {
                setLoadingLookups(false);
            }
        };
        loadLookups();
    }, []);

    const resetResults = () => {
        setPreviewBranches(null);
        setConnectError(null);
        setCreateResults(null);
        setCreateProgress(null);
    };

    const onClientChange = (value) => {
        setClient(value);
        resetResults();
    };

    const currentKey = () => client;

    useEffect(() => {
        if (!client) return undefined;
        let cancelled = false;
        const run = async () => {
            setPreviewLoading(true);
            try {
                const result = await previewBranchTables({ client_id: Number(client) });
                if (cancelled) return;
                if (result?.status === 'success') {
                    setPreviewBranches(result.branches || []);
                    setConnectError(result.connect_error || null);
                    setPreviewKey(currentKey());
                } else {
                    toast.current?.show({ severity: 'error', summary: 'Check failed', detail: result?.message || 'Unknown error' });
                }
            } catch (error) {
                if (!cancelled) toast.current?.show({ severity: 'error', summary: 'Check failed', detail: error?.response?.data?.message || error.message });
            } finally {
                if (!cancelled) setPreviewLoading(false);
            }
        };
        run();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client]);

    const previewStale = previewBranches !== null && previewKey !== currentKey();
    const missingCount = previewBranches
        ? previewBranches.reduce((s, b) => s + TABLE_BASES.filter((t) => !b.tables[t.key]).length, 0)
        : null;
    const canCreate = client && !createLoading && previewBranches && previewBranches.length > 0 && !connectError;

    const confirmCreate = () => {
        confirmDialog({
            header: 'Create missing billing tables?',
            message: (
                <p>
                    For client {client}, this creates whichever of the 3 per-branch billing tables (Bill Details, Billed Products, Payment Details) don&apos;t exist yet on that client&apos;s own
                    database — one physical table per branch, named after that branch&apos;s branch number. Tables that already exist are left completely untouched.
                </p>
            ),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Create Tables',
            rejectLabel: 'Cancel',
            accept: runCreate
        });
    };

    const runCreate = async () => {
        const branches = previewBranches || [];
        if (branches.length === 0) return;

        setCreateLoading(true);
        setCreateResults([]);
        const collected = [];

        try {
            for (let i = 0; i < branches.length; i++) {
                const branch = branches[i];
                setCreateProgress({ current: i, total: branches.length, label: `Checking ${branch.branch_name}…` });

                // eslint-disable-next-line no-await-in-loop
                const result = await createBranchTables({ client_id: Number(client), branch_ids: [branch.branch_id] });
                const rows = result?.status === 'success' ? result.results || [] : [{ branch_id: branch.branch_id, branch_name: branch.branch_name, tables: {}, error: result?.message }];

                collected.push(...rows);
                setCreateResults([...collected]);
                const next = branches[i + 1];
                setCreateProgress({ current: i + 1, total: branches.length, label: next ? `Checking ${next.branch_name}…` : 'Done' });
            }

            const anyError = collected.some((r) => Object.values(r.tables || {}).some((t) => t.status === 'error') || r.error);
            toast.current?.show({
                severity: anyError ? 'warn' : 'success',
                summary: 'Branch table setup finished',
                detail: anyError ? 'Completed with some tables failed — see results below' : 'All missing tables were created'
            });
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Create failed', detail: error?.response?.data?.message || error.message });
        } finally {
            setCreateLoading(false);
        }
    };

    const createSummary = createResults && {
        created: createResults.reduce((s, r) => s + Object.values(r.tables || {}).filter((t) => t.status === 'created').length, 0),
        already: createResults.reduce((s, r) => s + Object.values(r.tables || {}).filter((t) => t.status === 'exists').length, 0),
        failed: createResults.reduce((s, r) => s + Object.values(r.tables || {}).filter((t) => t.status === 'error').length, 0)
    };

    return (
        <div className="grid">
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="col-12">
                <Card>
                    <div className="flex align-items-center gap-3 mb-2">
                        <div className="flex align-items-center justify-content-center border-circle" style={{ width: 48, height: 48, background: 'linear-gradient(135deg,#f97316,#ef4444)' }}>
                            <i className="pi pi-table text-white text-xl" />
                        </div>
                        <div>
                            <div className="text-xl font-bold">Branch Tables — Billing Table Setup</div>
                            <div className="text-color-secondary text-sm">
                                Create the per-branch billing tables (Bill Details, Billed Products, Payment Details) a branch needs before its first POS sync can land any data.
                            </div>
                        </div>
                    </div>

                    <Divider />

                    <div className="border-round shadow-1 p-3 surface-card" style={{ borderTop: '4px solid #f97316', maxWidth: 420 }}>
                        <div className="flex align-items-center gap-2 mb-3">
                            <i className="pi pi-building text-orange-500" />
                            <span className="font-bold">Client</span>
                        </div>
                        <Dropdown value={client} options={clients} onChange={(e) => onClientChange(e.value)} placeholder="Select client" filter showClear className="w-full" loading={loadingLookups} />
                    </div>

                    {connectError && <Message severity="error" className="mt-3 w-full" text={connectError} />}
                    {previewLoading && <Message severity="info" className="mt-3 w-full" text="Checking client database..." />}
                    {previewStale && <Message severity="warn" className="mt-3 w-full" text="Selection changed — re-checking automatically." />}

                    {previewBranches && !previewLoading && previewBranches.length === 0 && (
                        <Message severity="warn" className="mt-3 w-full" text="This client has no branches in branch_creation yet." />
                    )}

                    {previewBranches && previewBranches.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-3">
                            <StatTile label="Branches" value={previewBranches.length} color="#6366f1" icon="pi-sitemap" />
                            <StatTile label="Tables Missing" value={missingCount} color="#f59e0b" icon="pi-exclamation-circle" />
                        </div>
                    )}

                    <div className="flex flex-wrap align-items-center gap-2 mt-4">
                        <Button label="Create Missing Tables" icon="pi pi-hammer" severity="warning" onClick={confirmCreate} loading={createLoading} disabled={!canCreate} />
                        <span className="text-color-secondary text-sm">Existing tables are never touched — only missing ones are created.</span>
                    </div>

                    {createProgress && (
                        <div className="mt-4 border-round p-3 surface-50">
                            <div className="flex justify-content-between align-items-center text-sm mb-2">
                                <span className="flex align-items-center gap-2">
                                    <i className={`pi ${createLoading ? 'pi-spin pi-spinner' : 'pi-check-circle'}`} style={{ color: '#f97316' }} />
                                    <span className="font-medium">{createProgress.label}</span>
                                </span>
                                <span className="text-color-secondary">
                                    {createProgress.current}/{createProgress.total}
                                </span>
                            </div>
                            <ProgressBar value={Math.round((createProgress.current / createProgress.total) * 100)} showValue={false} color="#f97316" style={{ height: 10 }} />
                        </div>
                    )}

                    {previewBranches && previewBranches.length > 0 && !createResults && (
                        <div className="mt-4">
                            <h5 className="mb-2">Branches &amp; Expected Tables</h5>
                            <p className="text-color-secondary text-sm mt-0 mb-2">
                                Table names are built as <code>{'{table}'}{'{branch_slno}'}</code> from branch_creation — e.g. branch no. 1 gets product_billdetail1, billed_product1,
                                payments_details1; branch no. 2 gets product_billdetail2, billed_product2, payments_details2, and so on.
                            </p>
                            <DataTable value={previewBranches} size="small" stripedRows className="border-round overflow-hidden">
                                <Column field="branch_name" header="Branch" />
                                <Column field="branch_slno" header="Branch No." />
                                {TABLE_BASES.map((t) => (
                                    <Column key={t.key} header={t.label} body={(row) => ExistTag(`${t.key}${row.branch_slno}`, row.tables[t.key])} />
                                ))}
                            </DataTable>
                        </div>
                    )}

                    {createResults && createResults.length > 0 && (
                        <div className="mt-4">
                            <h5 className="mb-2">Create Result</h5>
                            {createSummary && !createLoading && (
                                <div className="flex flex-wrap gap-3 mb-3">
                                    <StatTile label="Tables created" value={createSummary.created} color="#22c55e" icon="pi-check-circle" />
                                    <StatTile label="Already existed" value={createSummary.already} color="#6366f1" icon="pi-info-circle" />
                                    <StatTile label="Failed" value={createSummary.failed} color="#ef4444" icon="pi-times-circle" />
                                </div>
                            )}
                            <DataTable value={createResults} size="small" stripedRows className="border-round overflow-hidden">
                                <Column field="branch_name" header="Branch" />
                                <Column field="branch_slno" header="Branch No." />
                                {TABLE_BASES.map((t) => (
                                    <Column
                                        key={t.key}
                                        header={t.label}
                                        body={(row) => {
                                            const info = row.tables?.[t.key];
                                            if (!info) return '';
                                            const severity = info.status === 'created' ? 'success' : info.status === 'exists' ? 'info' : 'danger';
                                            const icon = info.status === 'created' ? 'pi pi-plus' : info.status === 'exists' ? 'pi pi-check' : 'pi pi-times';
                                            return <Tag severity={severity} icon={icon} value={`${t.key}${row.branch_slno}`} title={info.message} />;
                                        }}
                                    />
                                ))}
                            </DataTable>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default BranchTables;
